import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface CompanyEntitlement {
  status: string;
  plan_name: string | null;
  is_active: boolean;
  is_grace_period: boolean;
  grace_deadline: string | null;
  enforcement_state: 'full_access' | 'grace_period' | 'restricted' | 'no_subscription';
  is_trial: boolean;
  trial_end: string | null;
  trial_days_remaining: number | null;
  source: 'stripe' | 'override' | 'none';
}

/**
 * Hook to fetch the company's subscription entitlement state.
 * This is the single source of truth for whether the current company
 * has access to product features.
 * 
 * Mounted in EntitlementProvider so it's available across all authenticated routes.
 */
export function useEntitlement() {
  const { profile } = useAuth();

  const query = useQuery<CompanyEntitlement>({
    queryKey: ['entitlement', profile?.company_id],
    queryFn: async () => {
      // ensure_user_context sets the company context for the RPC
      await supabase.rpc('ensure_user_context');
      const { data, error } = await supabase.rpc('get_company_entitlement');
      if (error) throw error;
      return data as unknown as CompanyEntitlement;
    },
    enabled: !!profile?.company_id,
    staleTime: 5 * 60 * 1000, // 5 minutes — subscription changes are infrequent
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true, // Catch stale state when user returns to tab
    refetchOnReconnect: true,
    retry: 2,
  });

  const entitlement = query.data;

  return {
    entitlement,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    // Convenience accessors
    enforcementState: entitlement?.enforcement_state ?? 'no_subscription',
    isActive: entitlement?.is_active ?? false,
    isGracePeriod: entitlement?.is_grace_period ?? false,
    graceDeadline: entitlement?.grace_deadline ?? null,
    isRestricted: entitlement?.enforcement_state === 'restricted' || entitlement?.enforcement_state === 'no_subscription',
    planName: entitlement?.plan_name ?? null,
    isTrial: entitlement?.is_trial ?? false,
    trialEnd: entitlement?.trial_end ?? null,
    trialDaysRemaining: entitlement?.trial_days_remaining ?? null,
    source: entitlement?.source ?? 'none',
  };
}